import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  RefreshCw,
  Loader2,
  AlertTriangle,
  ArrowDownToLine,
  CheckCircle2,
  Info,
} from "lucide-react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { Badge } from "./ui/badge";

/**
 * Phase 101gb (Feb 2026) — Bouton « Resync stock Nayax ».
 *
 * Aligne le stock MoMa sur le stock AM en poussant une picklist NÉGATIVE
 * pour tous les produits où MoMa croit encore avoir des barquettes qu'AM
 * a déjà retirées (DLC, casse, blocage, transfert).
 *
 * Flow en 2 temps :
 *  1. Preview : GET /nayax-resync-stock/preview → tableau des deltas
 *  2. Push : POST /nayax-resync-stock → applique et log
 *
 * Props :
 *   distributorId — id AM du distributeur
 *   distributorName — pour toasts
 *   onDone — callback (optionnel) après un push réussi (ex: reload page)
 */
export default function NayaxResyncDialog({ distributorId, distributorName, onDone }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [pushResult, setPushResult] = useState(null);

  const loadPreview = useCallback(async () => {
    if (!distributorId) return;
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(`/distributors/${distributorId}/nayax-resync-stock/preview`);
      setPreview(r.data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Impossible de charger la prévisualisation Nayax");
    } finally {
      setLoading(false);
    }
  }, [distributorId]);

  useEffect(() => {
    if (open && !pushResult) {
      loadPreview();
    }
  }, [open, pushResult, loadPreview]);

  const doPush = async () => {
    setPushing(true);
    setError(null);
    try {
      const r = await api.post(`/distributors/${distributorId}/nayax-resync-stock`);
      setPushResult(r.data);
      const corrections = r.data?.corrections || [];
      const deferred = r.data?.deferred_dlc || [];
      const totalBarq = corrections.reduce((s, c) => s + Math.abs(c.delta || 0), 0);
      if (r.data?.ok) {
        if (corrections.length === 0 && deferred.length === 0) {
          toast.success(`✅ Tournée validée — tout est en sync`);
        } else if (corrections.length === 0 && deferred.length > 0) {
          toast.success(`✅ Tournée validée · ${deferred.length} sortie(s) DLC tracée(s) (auto-align au prochain reappro)`);
        } else {
          toast.success(`✅ Tournée validée · ${corrections.length} reappro(s) poussé(s) · ${totalBarq} barquette(s)${deferred.length ? ` · ${deferred.length} DLC tracée(s)` : ""}`);
        }
        if (onDone) onDone();
      } else {
        const errMsg = r.data?.error || r.data?.push_result?.error || "Échec du push Nayax";
        toast.error(errMsg);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Erreur lors du push Nayax";
      setError(msg);
      toast.error(msg);
    } finally {
      setPushing(false);
    }
  };

  const reset = () => {
    setPreview(null);
    setPushResult(null);
    setError(null);
  };

  const handleOpenChange = (v) => {
    setOpen(v);
    if (!v) {
      // Reset après fermeture pour recharger un preview frais la prochaine fois
      setTimeout(reset, 200);
    }
  };

  const summary = preview?.summary;
  const rows = preview?.rows || [];

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className="border-[#8C3A2A] text-[#8C3A2A] hover:bg-[#FBE5E0]"
          data-testid="nayax-resync-btn"
          title="Applique les reappros pushables + trace les sorties DLC (auto-alignement au prochain reappro)"
        >
          <ArrowDownToLine className="w-4 h-4 mr-2" /> Resync stock Nayax
        </Button>
      </DialogTrigger>

      <DialogContent
        className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
        data-testid="nayax-resync-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowDownToLine className="w-5 h-5 text-[#8C3A2A]" />
            Réconciliation stock Nayax — {distributorName}
          </DialogTitle>
        </DialogHeader>

        {/* Étape 3 : résultat du push */}
        {pushResult && (
          <div className="flex-1 overflow-y-auto space-y-4" data-testid="resync-result">
            <div className={`rounded-lg p-4 border ${pushResult.ok ? "bg-[#E8F1EA] border-[#B9D6BD]" : "bg-[#FBE5E0] border-[#E8C7BD]"}`}>
              <div className="flex items-center gap-2 mb-2">
                {pushResult.ok
                  ? <CheckCircle2 className="w-5 h-5 text-[#1F4E3D]" />
                  : <AlertTriangle className="w-5 h-5 text-[#8C3A2A]" />}
                <h3 className="font-semibold text-sm">
                  {pushResult.ok ? "Tournée validée avec succès" : "Validation tournée échouée"}
                </h3>
              </div>
              <div className="text-sm space-y-1">
                {pushResult.ok ? (
                  <>
                    {(pushResult.corrections || []).length === 0 && (pushResult.deferred_dlc || []).length === 0 ? (
                      <div className="text-[#1F4E3D]">{pushResult.reason || "Aucune correction nécessaire — tout est déjà aligné."}</div>
                    ) : (
                      <>
                        {(pushResult.corrections || []).length > 0 && (
                          <>
                            <div><strong>{(pushResult.corrections || []).length}</strong> reappro(s) poussé(s) vers Nayax</div>
                            <div><strong>{(pushResult.corrections || []).reduce((s, c) => s + Math.abs(c.delta || 0), 0)}</strong> barquette(s) ajoutée(s) côté Nayax</div>
                          </>
                        )}
                        {(pushResult.deferred_dlc || []).length > 0 && (
                          <div className="mt-2 pt-2 border-t border-white/40 text-[#2D4E7A]">
                            ⏳ <strong>{(pushResult.deferred_dlc || []).length}</strong> sortie(s) DLC différée(s) · auto-alignement au prochain reappro (cap PAR Nayax)
                          </div>
                        )}
                        {pushResult.push_result?.picklist_id && (
                          <div className="text-xs text-[#575A57]">Picklist Nayax : <code className="bg-white px-1.5 py-0.5 rounded">#{pushResult.push_result.picklist_id}</code></div>
                        )}
                      </>
                    )}
                  </>
                ) : (
                  <div className="text-[#8C3A2A]">
                    {pushResult.error || pushResult.push_result?.error || pushResult.reason || "Erreur inconnue"}
                  </div>
                )}
              </div>
            </div>

            {/* Phase 101ea.8 — Diagnostic détaillé quand le push a échoué */}
            {!pushResult.ok && (
              <NayaxDiagnosticBlock distributorId={distributorId} pushResult={pushResult} />
            )}

            {/* Détails des corrections appliquées (push_positive) */}
            {pushResult.ok && (pushResult.corrections || []).length > 0 && (
              <div className="border border-[#E5E5E0] rounded-lg overflow-hidden">
                <div className="bg-[#FAFAF7] px-3 py-2 text-[10px] uppercase tracking-wider text-[#575A57] font-semibold border-b border-[#E5E5E0]">
                  Reappros appliqués ({(pushResult.corrections || []).length})
                </div>
                <div className="max-h-[35vh] overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-[#FDFDFB] text-[10px] uppercase tracking-wider text-[#575A57]">
                      <tr>
                        <th className="px-2 py-1.5 text-left w-12">Slot</th>
                        <th className="px-2 py-1.5 text-left">Produit</th>
                        <th className="px-2 py-1.5 text-right w-14">AM</th>
                        <th className="px-2 py-1.5 text-right w-14">Nayax</th>
                        <th className="px-2 py-1.5 text-right w-14">Ajouté</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(pushResult.corrections || []).map((c, i) => (
                        <tr key={i} className="border-t border-[#F0F0EB]">
                          <td className="px-2 py-1 font-mono text-[10px]">{c.slot || "—"}</td>
                          <td className="px-2 py-1 font-medium">{c.product_name}</td>
                          <td className="px-2 py-1 text-right font-mono">{c.am_qty}</td>
                          <td className="px-2 py-1 text-right font-mono">{c.moma_qty}</td>
                          <td className="px-2 py-1 text-right font-mono font-semibold text-[#8C6B2F]">+{Math.abs(c.delta || 0)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Détails des sorties DLC différées */}
            {pushResult.ok && (pushResult.deferred_dlc || []).length > 0 && (
              <div className="border border-[#B5C7DE] rounded-lg overflow-hidden">
                <div className="bg-[#EAF0F8] px-3 py-2 text-[10px] uppercase tracking-wider text-[#2D4E7A] font-semibold border-b border-[#B5C7DE] flex items-center gap-1">
                  <Info className="w-3 h-3" /> Sorties DLC différées ({(pushResult.deferred_dlc || []).length}) · auto-alignement au prochain reappro
                </div>
                <div className="max-h-[30vh] overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-[#F5F8FB] text-[10px] uppercase tracking-wider text-[#575A57]">
                      <tr>
                        <th className="px-2 py-1.5 text-left w-12">Slot</th>
                        <th className="px-2 py-1.5 text-left">Produit</th>
                        <th className="px-2 py-1.5 text-right w-14">AM</th>
                        <th className="px-2 py-1.5 text-right w-14">Nayax</th>
                        <th className="px-2 py-1.5 text-right w-14">Δ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(pushResult.deferred_dlc || []).map((d, i) => (
                        <tr key={i} className="border-t border-[#F0F0EB]">
                          <td className="px-2 py-1 font-mono text-[10px]">{d.slot || "—"}</td>
                          <td className="px-2 py-1 font-medium">{d.product_name}</td>
                          <td className="px-2 py-1 text-right font-mono">{d.am_qty}</td>
                          <td className="px-2 py-1 text-right font-mono">{d.moma_qty}</td>
                          <td className="px-2 py-1 text-right font-mono font-semibold text-[#2D4E7A]">+{d.delta || 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="text-xs text-[#575A57] italic">
              Le prochain reappro depuis AM repartira maintenant du stock corrigé côté Nayax.
              Vérifiez visuellement la machine au prochain passage.
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t border-[#E5E5E0]">
              <Button variant="outline" onClick={() => handleOpenChange(false)} data-testid="resync-close-btn">
                Fermer
              </Button>
            </div>
          </div>
        )}

        {/* Étape 1 : preview */}
        {!pushResult && (
          <>
            {loading && (
              <div className="flex-1 flex items-center justify-center py-12" data-testid="resync-loading">
                <Loader2 className="w-6 h-6 animate-spin text-[#8C6B2F] mr-2" />
                <span className="text-sm text-[#575A57]">Analyse des écarts en cours…</span>
              </div>
            )}

            {error && !loading && (
              <div className="bg-[#FBE5E0] border border-[#E8C7BD] rounded-lg p-4 text-sm text-[#8C3A2A]" data-testid="resync-error">
                <AlertTriangle className="w-4 h-4 inline mr-2" />
                {error}
                <Button variant="link" onClick={loadPreview} className="text-[#8C3A2A] underline ml-2 p-0 h-auto">
                  Réessayer
                </Button>
              </div>
            )}

            {preview && !loading && !error && (
              <div className="flex-1 overflow-y-auto space-y-3">
                {/* Résumé */}
                {summary && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="resync-summary">
                    <SummaryCard
                      label="À pousser (reappro Nayax)"
                      value={summary.to_push}
                      intent={summary.to_push > 0 ? "warn" : "ok"}
                      testid="summary-to-push"
                    />
                    <SummaryCard
                      label="En sync"
                      value={summary.in_sync}
                      intent="ok"
                      testid="summary-in-sync"
                    />
                    <SummaryCard
                      label="Sorties DLC différées"
                      value={summary.deferred_dlc || 0}
                      intent={(summary.deferred_dlc || 0) > 0 ? "muted" : "muted"}
                      testid="summary-deferred-dlc"
                    />
                    <SummaryCard
                      label="Non mappés"
                      value={summary.unmapped}
                      intent={summary.unmapped > 0 ? "warn" : "muted"}
                      testid="summary-unmapped"
                    />
                  </div>
                )}

                {/* Info : sorties DLC gérées automatiquement */}
                {summary && (summary.deferred_dlc || 0) > 0 && (
                  <div className="bg-[#EAF0F8] border border-[#B5C7DE] rounded-lg p-3 text-xs text-[#2D4E7A] flex items-start gap-2" data-testid="deferred-dlc-info">
                    <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="font-semibold mb-1">
                        {summary.deferred_dlc} sortie(s) DLC : {summary.total_barquettes_deferred || 0} barquette(s) à retirer côté Nayax
                      </div>
                      <div>
                        L'API Nayax refuse les <code className="bg-white/50 px-1 rounded">PickQty</code> négatifs.
                        Ces sorties seront alignées <strong>automatiquement au prochain reappro</strong> grâce au cap PAR Nayax.
                        Pour un slot discontinué, corrige manuellement dans le portail Nayax.
                      </div>
                    </div>
                  </div>
                )}

                {summary && summary.to_push === 0 && (summary.deferred_dlc || 0) === 0 && summary.unmapped === 0 && (
                  <div className="bg-[#E8F1EA] border border-[#B9D6BD] rounded-lg p-4 text-sm text-[#1F4E3D] flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5" />
                    Tout est aligné — Nayax et AM sont d'accord sur les stocks. Rien à faire.
                  </div>
                )}

                {preview.note && (
                  <div className="text-xs text-[#575A57] italic flex items-center gap-1">
                    <Info className="w-3 h-3" /> {preview.note}
                  </div>
                )}

                {/* Table des deltas */}
                {rows.length > 0 && (
                  <div className="border border-[#E5E5E0] rounded-lg overflow-hidden">
                    <div className="overflow-x-auto max-h-[45vh]">
                      <table className="w-full text-xs" data-testid="resync-rows-table">
                        <thead className="bg-[#FAFAF7] text-[10px] uppercase tracking-wider text-[#575A57] sticky top-0">
                          <tr>
                            <th className="px-2 py-2 text-left w-12">Slot</th>
                            <th className="px-2 py-2 text-left">Produit</th>
                            <th className="px-2 py-2 text-right w-14">AM</th>
                            <th className="px-2 py-2 text-right w-14">Nayax</th>
                            <th className="px-2 py-2 text-right w-14">Δ</th>
                            <th className="px-2 py-2 text-center w-32">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((r, i) => (
                            <ResyncRow key={`${r.nayax_product_id}-${i}`} row={r} />
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Explications actions */}
                <div className="bg-[#F8F8F5] border border-[#E5E5E0] rounded-lg p-3 text-[11px] text-[#575A57] space-y-1">
                  <div><Badge className="bg-[#FBEEDB] text-[#8C6B2F] border-[#E8D3A8] mr-2 text-[10px]">push_positive</Badge> Nayax sous-estime {"→"} on ajoute virtuellement les barquettes manquantes</div>
                  <div><Badge className="bg-[#E8F1EA] text-[#1F4E3D] border-[#B9D6BD] mr-2 text-[10px]">in_sync</Badge> Nayax et AM sont d'accord {"—"} rien à faire</div>
                  <div><Badge className="bg-[#EAF0F8] text-[#2D4E7A] border-[#B5C7DE] mr-2 text-[10px]">deferred_next_reappro</Badge> Sortie DLC : Nayax refuse les négatifs {"→"} aligné au prochain reappro (cap PAR)</div>
                  <div><Badge className="bg-[#F0F0EB] text-[#575A57] border-[#E5E5E0] mr-2 text-[10px]">unmapped</Badge> NayaxProductID sans produit AM associé {"—"} configurer d'abord le mapping</div>
                </div>
              </div>
            )}

            {/* Actions bar */}
            <div className="flex justify-between items-center gap-2 pt-3 border-t border-[#E5E5E0] mt-2">
              <Button
                variant="outline"
                onClick={loadPreview}
                disabled={loading || pushing}
                size="sm"
                data-testid="resync-refresh-preview-btn"
              >
                <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />
                Rafraîchir
              </Button>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => handleOpenChange(false)}
                  disabled={pushing}
                  data-testid="resync-cancel-btn"
                >
                  Annuler
                </Button>
                <Button
                  onClick={doPush}
                  // Phase 101ea.11 : le livreur doit TOUJOURS pouvoir fermer sa
                  // tournée, même quand il n'y a rien à pusher (tournée = que du
                  // retrait DLC → deferred, ou tout aligné → in_sync).
                  // Sinon bouton grisé = panique + tournée non validée.
                  disabled={loading || pushing || !summary}
                  className="bg-[#8C3A2A] hover:bg-[#6E2D1F] text-white"
                  data-testid="resync-push-btn"
                >
                  {pushing ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Envoi en cours…</>
                  ) : summary?.to_push > 0 ? (
                    <><ArrowDownToLine className="w-4 h-4 mr-2" /> Pousser vers Nayax ({summary.to_push})</>
                  ) : (summary?.deferred_dlc || 0) > 0 ? (
                    <><CheckCircle2 className="w-4 h-4 mr-2" /> Valider tournée ({summary.deferred_dlc} DLC tracées)</>
                  ) : (
                    <><CheckCircle2 className="w-4 h-4 mr-2" /> Valider tournée (tout est en sync)</>
                  )}
                </Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SummaryCard({ label, value, intent = "muted", testid }) {
  const styles = {
    ok:     { bg: "#E8F1EA", border: "#B9D6BD", text: "#1F4E3D" },
    warn:   { bg: "#FBEEDB", border: "#E8D3A8", text: "#8C6B2F" },
    danger: { bg: "#FBE5E0", border: "#E8C7BD", text: "#8C3A2A" },
    muted:  { bg: "#F0F0EB", border: "#E5E5E0", text: "#575A57" },
  }[intent] || {};
  return (
    <div
      className="rounded-lg p-2.5 border"
      style={{ backgroundColor: styles.bg, borderColor: styles.border }}
      data-testid={testid}
    >
      <div className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: styles.text }}>{label}</div>
      <div className="text-xl font-heading mt-0.5" style={{ color: styles.text }}>{value ?? 0}</div>
    </div>
  );
}

function ResyncRow({ row }) {
  const actionBadges = {
    push_positive:         { label: "Push (reappro)", bg: "#FBEEDB", border: "#E8D3A8", text: "#8C6B2F" },
    in_sync:               { label: "En sync",         bg: "#E8F1EA", border: "#B9D6BD", text: "#1F4E3D" },
    deferred_next_reappro: { label: "Différé DLC",     bg: "#EAF0F8", border: "#B5C7DE", text: "#2D4E7A" },
    unmapped:              { label: "Non mappé",       bg: "#F0F0EB", border: "#E5E5E0", text: "#575A57" },
    // Legacy — anciennes réponses avant Phase 101ea.10
    push_negative:         { label: "Différé DLC",     bg: "#EAF0F8", border: "#B5C7DE", text: "#2D4E7A" },
    skip_moma_below_am:    { label: "Push (reappro)",  bg: "#FBEEDB", border: "#E8D3A8", text: "#8C6B2F" },
  };
  const badge = actionBadges[row.action] || actionBadges.unmapped;
  const deltaSign = row.delta > 0 ? "+" : "";
  const deltaColor = row.delta > 0 ? "#8C3A2A" : (row.delta < 0 ? "#8C6B2F" : "#575A57");
  return (
    <tr className="border-t border-[#F0F0EB] hover:bg-[#FAFAF7]">
      <td className="px-2 py-1.5 font-mono text-[10px]">{row.slot || "—"}</td>
      <td className="px-2 py-1.5">
        <div className="font-medium">{row.product_name}</div>
        {row.sku && <div className="text-[10px] text-[#A3A6A3]">{row.sku}</div>}
      </td>
      <td className="px-2 py-1.5 text-right font-mono">{row.am_qty ?? "—"}</td>
      <td className="px-2 py-1.5 text-right font-mono">{row.moma_qty ?? "—"}</td>
      <td className="px-2 py-1.5 text-right font-mono font-semibold" style={{ color: deltaColor }}>
        {row.delta === null || row.delta === undefined ? "—" : `${deltaSign}${row.delta}`}
      </td>
      <td className="px-2 py-1.5 text-center">
        <span
          className="inline-block px-2 py-0.5 rounded border text-[10px] font-medium"
          style={{ backgroundColor: badge.bg, borderColor: badge.border, color: badge.text }}
        >
          {badge.label}
        </span>
      </td>
    </tr>
  );
}


// Phase 101ea.8 — Bloc diagnostic AM ↔ MoMa quand le push échoue.
// Chargé à la demande via un bouton "Charger le diagnostic".
function NayaxDiagnosticBlock({ distributorId, pushResult: _pushResult }) {
  const [diagnostic, setDiagnostic] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState(null);

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      const r = await api.get(`/distributors/${distributorId}/nayax-diagnostic`);
      setDiagnostic(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Erreur chargement diagnostic");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="border border-[#E8C7BD] rounded-lg bg-[#FDF7F5] p-3 space-y-2" data-testid="nayax-diagnostic-block">
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm font-semibold text-[#8C3A2A]">
          🔍 Diagnostic complet AM ↔ Nayax
        </div>
        {!diagnostic && (
          <Button
            size="sm"
            variant="outline"
            onClick={load}
            disabled={loading}
            className="border-[#8C3A2A] text-[#8C3A2A] hover:bg-[#FBE5E0] text-xs"
            data-testid="nayax-diagnostic-load-btn"
          >
            {loading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
            Charger le diagnostic
          </Button>
        )}
      </div>

      {!diagnostic && !loading && (
        <div className="text-xs text-[#575A57]">
          Charge une vue slot-par-slot pour repérer le décalage entre ce qu'AM veut pusher et ce que Nayax expose.
        </div>
      )}
      {err && <div className="text-xs text-[#8C3A2A]">{err}</div>}

      {diagnostic && (
        <div className="space-y-3">
          {diagnostic.anomalies_count === 0 ? (
            <div className="text-xs text-[#1F4E3D] italic">
              Aucune anomalie structurelle détectée. Le push échoue pour une autre raison — regarde le message Nayax brut ci-dessus.
            </div>
          ) : (
            <div className="space-y-1.5">
              <div className="text-xs font-semibold text-[#8C3A2A]">
                ⚠ {diagnostic.anomalies_count} anomalie(s) détectée(s) :
              </div>
              {(diagnostic.anomalies || []).map((a, i) => (
                <div
                  key={i}
                  className="border border-[#E8C7BD] rounded p-2 bg-white text-xs"
                  data-testid={`nayax-diagnostic-anomaly-${i}`}
                >
                  <div className="flex items-center gap-2">
                    <span className="font-mono bg-[#FBE5E0] text-[#8C3A2A] px-1.5 py-0.5 rounded text-[10px]">
                      slot {a.slot || "?"}
                    </span>
                    <span className="text-[10px] uppercase tracking-wider text-[#575A57] font-semibold">{a.type}</span>
                  </div>
                  <div className="text-[#1A1C1A] mt-0.5">{a.detail}</div>
                  <div className="text-[#575A57] italic mt-0.5 text-[10px]">→ {a.impact}</div>
                </div>
              ))}
            </div>
          )}

          {/* Vue slot-par-slot MoMa */}
          <details className="text-xs">
            <summary className="cursor-pointer text-[#575A57] font-medium">
              Slots Nayax exposés ({(diagnostic.moma_slots || []).length})
            </summary>
            <div className="mt-1 border border-[#E5E5E0] rounded overflow-x-auto max-h-56 overflow-y-auto">
              <table className="w-full text-[10px]">
                <thead className="bg-[#FAFAF7] text-[9px] uppercase text-[#575A57]">
                  <tr>
                    <th className="px-2 py-1 text-left">Slot</th>
                    <th className="px-2 py-1 text-left">NPID</th>
                    <th className="px-2 py-1 text-left">MPID</th>
                    <th className="px-2 py-1 text-left">DEX name</th>
                    <th className="px-2 py-1 text-right">PAR</th>
                    <th className="px-2 py-1 text-right">Missing</th>
                  </tr>
                </thead>
                <tbody>
                  {(diagnostic.moma_slots || []).map((s, i) => (
                    <tr key={i} className="border-t border-[#F0F0EB]">
                      <td className="px-2 py-1 font-mono">{s.slot || "—"}</td>
                      <td className="px-2 py-1 font-mono">{s.nayax_product_id ?? "—"}</td>
                      <td className={`px-2 py-1 font-mono ${s.machine_product_id === null || s.machine_product_id === undefined ? "text-[#8C3A2A] font-bold" : ""}`}>
                        {s.machine_product_id ?? "❌ null"}
                      </td>
                      <td className="px-2 py-1 truncate max-w-[180px]">{s.dex_name || "—"}</td>
                      <td className="px-2 py-1 text-right font-mono">{s.par ?? "—"}</td>
                      <td className="px-2 py-1 text-right font-mono">{s.missing_by_mdb ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>

          {/* Lots AM par slot */}
          <details className="text-xs">
            <summary className="cursor-pointer text-[#575A57] font-medium">
              Lots AM actifs par slot ({Object.keys(diagnostic.am_lots_by_slot || {}).length} slot(s))
            </summary>
            <div className="mt-1 space-y-1">
              {Object.entries(diagnostic.am_lots_by_slot || {}).map(([slot, lotsList]) => (
                <div key={slot} className="border border-[#E5E5E0] rounded p-1.5 bg-white">
                  <div className="font-mono text-[10px] font-semibold text-[#1A1C1A]">Slot {slot}</div>
                  {lotsList.map((lt) => (
                    <div key={lt.lot_id} className="text-[10px] text-[#575A57] pl-3">
                      • {lt.product_name} — {lt.quantity} barq. · NPID={lt.product_nayax_product_id ?? "❌ absent"} · lot {lt.lot_number} · DLC {lt.dlc || "—"}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </details>
        </div>
      )}
    </div>
  );
}
